/**
 * SettingsToc — Phase 4c+++++++ (Feb 2026)
 *
 * Sticky-Sidebar mit Inhaltsverzeichnis zur schnellen Navigation zwischen
 * den Settings-Sektionen. Anchor-Scroll zu `#settings-<key>` IDs.
 * Desktop: vertikale Liste links. Mobile: horizontal scrollbare Chips.
 *
 * Sections-Definition wird vom Aufrufer übergeben und gefiltert nach Rolle.
 */
import { useState, useEffect } from "react";
import { ChevronRight, Search, ChevronsDownUp, ChevronsUpDown } from "lucide-react";

export default function SettingsToc({ sections, onJump }) {
    const [activeId, setActiveId] = useState(sections?.[0]?.id || null);
    // Jul-2026 (User-Wunsch) — Universalsuche + Alles ein-/ausklappen
    const [query, setQuery] = useState("");
    const q = query.trim().toLowerCase();
    const visible = q ? sections.filter((s) => s.label.toLowerCase().includes(q)) : sections;
    const toggleAll = (open) => {
        window.dispatchEvent(new CustomEvent("twowheels:settings-expand-all", { detail: { open } }));
        if (open) onJump?.("__expand_all__");
    };

    // IntersectionObserver für Active-State der TOC
    useEffect(() => {
        if (!sections || sections.length === 0) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        setActiveId(e.target.id);
                    }
                });
            },
            { rootMargin: "-100px 0px -60% 0px", threshold: 0.05 },
        );
        sections.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [sections]);

    const jumpTo = (id) => {
        onJump?.(id);
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: "smooth" });
        setActiveId(id);
    };

    return (
        <nav data-testid="settings-toc" aria-label="Einstellungen — Inhaltsverzeichnis">
            {/* Mobile / Tablet: horizontal scrolling chips */}
            <div className="lg:hidden -mx-4 px-4 mb-3 overflow-x-auto">
                <div className="flex gap-1.5 pb-2">
                    {visible.map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => jumpTo(s.id)}
                            className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-sm border whitespace-nowrap ${
                                activeId === s.id
                                    ? "bg-[#111827] text-[#FFCC00] border-[#111827]"
                                    : "bg-white text-[#111827] border-gray-300"
                            }`}
                            data-testid={`toc-chip-${s.id}`}
                        >
                            {s.Icon && <s.Icon className="w-3 h-3 mr-1 inline -mt-0.5" />}
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Desktop: sticky vertical sidebar */}
            <div className="hidden lg:block sticky top-4">
                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                    <div className="px-3 py-2 bg-[#111827] text-[#FFCC00] text-[10px] uppercase tracking-wider font-black flex items-center justify-between">
                        <span>Inhalt</span>
                        <span className="flex items-center gap-1">
                            <button type="button" onClick={() => toggleAll(true)} title="Alles ausklappen"
                                className="p-1 rounded-sm hover:bg-white/10" data-testid="settings-expand-all">
                                <ChevronsUpDown className="w-3.5 h-3.5" />
                            </button>
                            <button type="button" onClick={() => toggleAll(false)} title="Alles einklappen"
                                className="p-1 rounded-sm hover:bg-white/10" data-testid="settings-collapse-all">
                                <ChevronsDownUp className="w-3.5 h-3.5" />
                            </button>
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-gray-100">
                        <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && visible[0]) { jumpTo(visible[0].id); setQuery(""); } }}
                            placeholder="Bereich suchen…"
                            className="w-full text-xs outline-none bg-transparent"
                            data-testid="settings-toc-search"
                        />
                    </div>
                    <ul className="py-1">
                        {visible.length === 0 && (
                            <li className="px-3 py-2 text-[11px] text-gray-400" data-testid="settings-toc-noresults">Kein Bereich gefunden.</li>
                        )}
                        {visible.map((s) => {
                            const Icon = s.Icon;
                            const isActive = activeId === s.id;
                            return (
                                <li key={s.id}>
                                    <button
                                        type="button"
                                        onClick={() => jumpTo(s.id)}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-bold transition-colors ${
                                            isActive
                                                ? "bg-[#FFCC00]/10 text-[#111827] border-l-2 border-[#FFCC00]"
                                                : "border-l-2 border-transparent text-gray-700 hover:bg-gray-50"
                                        }`}
                                        data-testid={`toc-link-${s.id}`}
                                    >
                                        {Icon && <Icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? "text-[#FFCC00]" : "text-gray-400"}`} />}
                                        <span className="flex-1 truncate">{s.label}</span>
                                        {isActive && <ChevronRight className="w-3 h-3 text-[#FFCC00]" />}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </nav>
    );
}
